import { useSettings } from '@capsicum/common';
import { useNCWBalance } from '@capsicum/socios-core';
import { useBrowserTransaction } from '@socios-blk/browser';
import { type PlatformTransactionExecutionParams } from '@socios-blk/core';
import { type NetworkFeeProps } from 'libs/common-web/ui/molecules';
import { useCallback, useEffect, useMemo } from 'react';

type SurveyVoteTransactionParams = {
	answerId: string;
	currencyDecimalDetail?: number | string;
	currencyShortName?: string;
	onTransactionError: (error?: unknown) => void;
	onTransactionFailure: () => void;
	onTransactionSuccess: (txHash?: string) => void;
	stakeAmount?: string;
	stakingContractAddress?: string;
	surveyContractAddress?: string;
	tokenContractAddress?: string;
};

/**
 * For the purpose of the MVP, the weight of the survey will be capped to 1.
 */
const SURVEY_WEIGHT = 1;

/**
 * Hook that handles the transaction logic for voting on a survey.
 * @param params {SurveyVoteTransactionParams} - The parameters required to build the transaction.
 * @returns The transaction logic.
 */
export const useSurveyVoteTransaction = (params: SurveyVoteTransactionParams) => {
	const {
		settings: { isTransactionSponsored }
	} = useSettings();
	const { getTokenBalanceBy } = useNCWBalance();
	const { estimateGasFee, gasFeeEstimate, isError, isPending, sendTransaction, TRANSACTIONS, userOpStatus } =
		useBrowserTransaction();

	const isTransactionLoading = !gasFeeEstimate || isPending;
	const isButtonDisabled = isTransactionLoading || !!userOpStatus?.transactionHash;

	const stakedAmount = Number(getTokenBalanceBy(tb => tb.shortCode === params.currencyShortName)?.stakedAmount) ?? 0;
	const isApproveRequired = stakedAmount < SURVEY_WEIGHT;

	/**
	 * The paymaster parameters required to sponsor the transaction.
	 */
	const paymasterParams: PlatformTransactionExecutionParams['paymasterParams'] = {
		isPaymasterConfigurationEnabled: isTransactionSponsored.survey,
		paymasterWebhookSecretParams: {
			parameters: [],
			target: 'survey_v3_vote'
		}
	};

	/**
	 * Determines whether to show the gas estimate as sponsored or normal.
	 */
	const gasFeeEstimateVariant: NetworkFeeProps['variant'] = isTransactionSponsored.survey ? 'free' : 'normal';

	/**
	 * Builds the batched transactions based on the need to stake prior to voting on the survey.
	 */
	const transaction = useMemo(() => {
		if (
			!params.surveyContractAddress ||
			!params.tokenContractAddress ||
			!params.stakingContractAddress ||
			!params.answerId ||
			!params.stakeAmount ||
			params.currencyDecimalDetail === undefined
		) {
			return undefined;
		}

		const voteTransaction = TRANSACTIONS.SURVEY_VOTE({
			answerId: params.answerId,
			decimals: params.currencyDecimalDetail,
			stakeAmount: params.stakeAmount,
			surveyContractAddress: params.surveyContractAddress
		});

		const approveTransaction = TRANSACTIONS.APPROVE({
			contractAddress: params.tokenContractAddress,
			decimals: params.currencyDecimalDetail,
			spender: params.stakingContractAddress,
			value: params.stakeAmount
		});

		return isApproveRequired ? [approveTransaction, voteTransaction] : [voteTransaction];
	}, [TRANSACTIONS, params, isApproveRequired]);

	/**
	 * Estimate the gas fee for the vote transaction once page loads and necessary params are present.
	 */
	useEffect(() => {
		if (!transaction || gasFeeEstimate || isPending || isError) {
			return;
		}

		estimateGasFee({
			onError: err => params.onTransactionError(err),
			paymasterParams,
			transaction
		});
	}, [transaction?.length, gasFeeEstimate, isPending, isError]);

	/**
	 * Send the vote transaction to the blockchain.
	 */
	const sendVoteTransaction = useCallback(async () => {
		if (!transaction) {
			params.onTransactionError(new Error('Transaction is undefined'));

			return;
		}

		await sendTransaction({
			onError: err => params.onTransactionError(err),
			onSuccess: ok => params.onTransactionSuccess(ok.transactionHash),
			paymasterParams,
			transaction
		});
	}, [transaction, sendTransaction, params]);

	return {
		gasFeeEstimate,
		gasFeeEstimateVariant,
		isButtonDisabled,
		isPending,
		isTransactionLoading,
		sendVoteTransaction,
		userOpStatus
	};
};
